/**
 * Log Service API client.
 * Communicates with the log service for streaming logs via Server-Sent Events.
 */

import { useCallback } from 'react';
import { getEnvVar } from '../utils/env';
import { withAuth } from './auth';

const isDevelopment = process.env.NODE_ENV === 'development';

export const LOG_SERVICE_URL =
  getEnvVar('REACT_APP_LOG_SERVICE_URL') ||
  (isDevelopment ? 'http://localhost:8002' : '');

/**
 * Stream logs from the log service.
 * Uses fetch + ReadableStream for SSE (EventSource doesn't support custom headers).
 *
 * @param {Object} options
 * @param {string} [options.service] - Filter by service name
 * @param {string} [options.level] - Filter by log level
 * @param {boolean} [options.fromStart=false] - If true, stream all logs in buffer first, then new ones
 * @param {Function} [options.onLog] - Callback for each log: (log) => void
 * @param {AbortSignal} [options.signal] - AbortSignal to cancel the stream
 * @param {string} [accessToken] - Auth0 access token (required when not using apiCall)
 * @returns {Promise<{ abort: () => void }>} Object with abort() to stop the stream
 */
export async function streamLogs(
  { service, level, fromStart = false, onLog, signal },
  accessToken
) {
  const params = new URLSearchParams();
  if (service) params.set('service', service);
  if (level) params.set('level', level);
  if (fromStart) params.set('from_start', 'true');
  const query = params.toString();
  const url = `${LOG_SERVICE_URL}/logs/stream${query ? `?${query}` : ''}`;

  const headers = { Accept: 'text/event-stream' };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const controller = new AbortController();
  if (signal) {
    signal.addEventListener('abort', () => controller.abort());
  }

  const response = await fetch(url, {
    method: 'GET',
    headers,
    signal: controller.signal,
  });

  if (!response.ok) {
    throw new Error(`Log stream failed: ${response.status} ${response.statusText}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const readStream = async () => {
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6).trim();
            if (jsonStr) {
              try {
                const log = JSON.parse(jsonStr);
                onLog?.(log);
              } catch {
                // Backend may send Python dict format (single quotes) - pass raw for display
                onLog?.({ message: jsonStr });
              }
            }
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        throw err;
      }
    }
  };

  readStream();

  return {
    abort: () => controller.abort(),
  };
}

/**
 * Stream an AI-generated summary of logs in the given timeframe.
 *
 * @param {Object} options
 * @param {string} options.start - Start of timeframe (ISO 8601)
 * @param {string} options.end - End of timeframe (ISO 8601)
 * @param {Function} [options.onChunk] - Callback for each text chunk: (text) => void
 * @param {Function} [options.onError] - Callback for stream error: (error) => void
 * @param {Function} [options.onComplete] - Callback when stream completes
 * @param {string} [accessToken] - Auth0 access token (required when not using apiCall)
 * @returns {Promise<{ abort: () => void }>} Object with abort() to stop the stream
 */
export async function summarizeLogs(
  { start, end, onChunk, onError, onComplete },
  accessToken
) {
  const params = new URLSearchParams({ start, end });
  const url = `${LOG_SERVICE_URL}/logs/summarize?${params.toString()}`;

  const headers = { Accept: 'text/event-stream' };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const controller = new AbortController();

  const response = await fetch(url, {
    method: 'GET',
    headers,
    signal: controller.signal,
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Log summarize failed: ${response.status} ${response.statusText}${body ? ` - ${body}` : ''}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const readStream = async () => {
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6).trim();
            if (jsonStr) {
              try {
                const data = JSON.parse(jsonStr);
                if (data.error) {
                  onError?.(data.error);
                } else if (data.text) {
                  onChunk?.(data.text);
                }
              } catch {
                // Ignore malformed chunks
              }
            }
          }
        }
      }
      onComplete?.();
    } catch (err) {
      if (err.name !== 'AbortError') {
        onError?.(err.message || 'Stream failed');
      }
    }
  };

  readStream();

  return {
    abort: () => controller.abort(),
  };
}

/**
 * Hook that provides Log Service API functions with authentication.
 * Must be called with apiCall from useApi (or equivalent).
 *
 * @param {Function} apiCall - The shared apiCall wrapper that handles auth and error redirects
 * @returns {Object} Log API functions
 */
export function useLogApi(apiCall) {
  const streamLogsWithAuth = useCallback(
    async (options = {}) => {
      return await apiCall(async (token) => {
        return await streamLogs(options, token);
      });
    },
    [apiCall]
  );

  const summarizeLogsWithAuth = useCallback(
    async (options = {}) => {
      return await apiCall(async (token) => {
        return await summarizeLogs(options, token);
      });
    },
    [apiCall]
  );

  return {
    streamLogs: streamLogsWithAuth,
    summarizeLogs: summarizeLogsWithAuth,
  };
}
